import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import PublicHeader from "../components/PublicHeader";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(token ? "" : "Token mancante nell'URL. Richiedi un nuovo link.");

  useEffect(() => {
    if (!token) setError("Token mancante nell'URL. Richiedi un nuovo link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("La password deve avere almeno 6 caratteri.");
      return;
    }
    if (password !== confirm) {
      setError("Le due password non corrispondono.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password aggiornata!");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grain flex flex-col">
      <PublicHeader ctaTo="/login" ctaLabel="Accedi" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🍱</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">
            Nuova password
          </h1>
          <p className="text-sm text-muted-foreground">
            Scegli una password sicura per il tuo account Schiscetta.
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-7 sm:p-8">
          {success ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-3" />
              <h2 className="font-display text-xl font-medium mb-2">Password aggiornata!</h2>
              <p className="text-sm text-muted-foreground">
                Ti sto portando al login…
              </p>
            </div>
          ) : !token ? (
            <div className="text-center py-4" data-testid="reset-no-token">
              <AlertTriangle className="w-12 h-12 text-accent mx-auto mb-3" />
              <h2 className="font-display text-xl font-medium mb-2">Link non valido</h2>
              <p className="text-sm text-muted-foreground mb-5">
                Manca il token di recupero. Richiedi un nuovo link.
              </p>
              <Link
                to="/forgot-password"
                data-testid="reset-cta-forgot"
                className="inline-flex px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343]"
              >
                Richiedi nuovo link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Nuova password</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    className="w-full px-3 py-2.5 pr-10 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                    aria-label={showPwd ? "Nascondi" : "Mostra"}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Almeno 6 caratteri.</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Conferma password</label>
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                />
              </div>
              {error && (
                <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md p-3" data-testid="reset-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="w-full py-2.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] disabled:opacity-50 transition-all"
              >
                {loading ? "Aggiornamento…" : "Salva nuova password"}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">
          <Link to="/login" className="text-primary hover:underline" data-testid="reset-link-login">
            Torna al login
          </Link>
        </p>
      </div>
      </div>
    </div>
  );
}
